import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardFooter,
  Col,
  Container,
  FormGroup,
  Label,
  Row,
  Button,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup"; // For validation
import Breadcrumbs from "../../../common-elements/breadcrumbs/Breadcrumbs";
import { H5, H6 } from "../../../AbstractElements";
import { PaymentGatewayData } from "./Payment";
import ButtonSection from "../../organization/create-organization/ButtonSection";

interface PaymentGateway {
  payment_gateway_id: number;
  gateway_name: string;
  sandbox_payment_url?: string;
  sandbox_status_url?: string;
  sandbox_refund_url?: string;
  sandbox_recharge_url?: string;
  live_payment_url?: string;
  live_status_url?: string;
  live_refund_url?: string;
  live_recharge_url?: string;
}

const validationSchema = Yup.object().shape({
  gateway_name: Yup.string().required("Gateway Name is required"),
  sandbox_payment_url:Yup.string().required("Sandbox payment URL is required"),
  live_payment_url:Yup.string().required("Live payment URL is required")
});

function EditPaymentSection() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState<PaymentGateway | null>(
    null
  );

  useEffect(() => {
    const gatewayToEdit = PaymentGatewayData.find(
      (gateway) => gateway.payment_gateway_id === parseInt(id || "0")
    );
    if (gatewayToEdit) {
      setInitialValues(gatewayToEdit);
    }
  }, [id]);

  const handleSubmit = (values: PaymentGateway) => {
    console.log("Saving Formik Data:", values);
  };
  const handleClose=()=>{
    navigate('/mastersettings/paymentgateway')
  }


  if (!initialValues) {
    return (
      <>
        <Breadcrumbs mainTitle={t("Loading...")} />
        <Container fluid>
          <Card className="mt-4 text-center p-5">
            <H5>Loading gateway data or gateway not found...</H5>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <Breadcrumbs
        mainTitle={t("Edit Payment Gateway")}
        parents={[
          { label: "MasterSettings" },
          {
            label: t("PaymentGateway"),
            link: `${process.env.PUBLIC_URL}/mastersettings/paymentgateway`,
          },
        ]}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {() => (
          <Form className="theme-form">
            <Container fluid>
              <Row>
                <Col xl="12">
                  <Card>
                    <CardBody>
                    
                      <Row>
                        <Col xl={12}>
                          <FormGroup>
                            <Label for="gateway_name">
                              {t("GatewayName")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              name="gateway_name"
                              type="text"
                              className="form-control"
                              disabled
                            />
                            <ErrorMessage
                              name="gateway_name"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <div className="p-3 border rounded mb-4">
                        <H6 className="text-primary mb-3">
                          {t("SandboxURLs")}
                        </H6>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="sandbox_payment_url">
                                {t("PaymentURL")}
                                <span className="txt-danger">*</span>
                              </Label>
                              <Field
                                name="sandbox_payment_url"
                                type="url"
                                className="form-control"
                              />
                               <ErrorMessage
                              name="sandbox_payment_url"
                              component="span"
                              className="text-danger"
                            />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="sandbox_status_url">
                                {t("StatusURL")}
                              </Label>
                              <Field
                                name="sandbox_status_url"
                                type="url"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="sandbox_refund_url">
                                {t("RefundURL")}
                              </Label>
                              <Field
                                name="sandbox_refund_url"
                                type="url"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="sandbox_recharge_url">
                                {t("RechargeURL")}
                              </Label>
                              <Field
                                name="sandbox_recharge_url"
                                type="url"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>

                      <div className="p-3 border rounded">
                        <H6 className="text-success mb-3">{t("LiveURLs")}</H6>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="live_payment_url">
                                {t("PaymentURL")}
                                <span className="txt-danger">*</span>
                              </Label>
                              <Field
                                name="live_payment_url"
                                type="url"
                                className="form-control"
                              />
                                <ErrorMessage
                              name="live_payment_url"
                              component="span"
                              className="text-danger"
                            />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="live_status_url">
                                {t("StatusURL")}
                              </Label>
                              <Field
                                name="live_status_url"
                                type="url"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="live_refund_url">
                                {t("RefundURL")}
                              </Label>
                              <Field
                                name="live_refund_url"
                                type="url"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="live_recharge_url">
                                {t("RechargeURL")}
                              </Label>
                              <Field
                                name="live_recharge_url"
                                type="url"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>
                    </CardBody>
                    <CardFooter className="text-end border-0 p-3">
                    <ButtonSection onClose={handleClose}/>
                    </CardFooter>
                  </Card>
                </Col>
              </Row>
            </Container>
          </Form>
        )}
      </Formik>
    </>
  );
}

export default EditPaymentSection;
